import { Component, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControlNames } from '../../shared/component/enums/FormControlNames';
import { ResetPassword } from '../../shared/models/rest-password.model';
import { NotificationService } from '../../shared/services/notification.service';
import { PasswordErrorService } from '../../shared/services/password-error.service';
import { PasswordRecoveryService } from '../password-recovery/services/password-recovery.service';

@Component({
  selector: 'app-password-reset',
  templateUrl: './password-reset.component.html',
  styleUrls: ['./password-reset.component.scss'],
  standalone: false,
})
export class PasswordResetComponent implements OnInit {
  resetEnabled = false;
  username!: string;
  token!: string;
  resetPassword: ResetPassword = {} as ResetPassword;
  passwordForm!: FormGroup;
  passwordControl!: FormControl;

  constructor(
    private passwordErrorService: PasswordErrorService,
    private router: Router,
    private route: ActivatedRoute,
    private passRecoveryService: PasswordRecoveryService,
    private notificationService: NotificationService,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.username = params['username'];
      this.token = params['passwordResetToken'];
    });
  }

  private initForm(): void {
    this.passwordControl = this.passwordErrorService.createFormControl();
    const confirmPasswordControl = new FormControl('', [Validators.required]);

    this.passwordForm = new FormGroup(
      {
        [FormControlNames.PASSWORD]: this.passwordControl,
        [FormControlNames.CONFIRM_PASSWORD]: confirmPasswordControl,
      },
      {
        validators: this.passwordErrorService.createPasswordMatchValidator(FormControlNames.PASSWORD, FormControlNames.CONFIRM_PASSWORD),
      },
    );

    this.passwordControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.resetPassword.password1 = value;
    });

    confirmPasswordControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (value != null) {
        this.resetPassword.password2 = value;
      }
    });
  }

  /** Return error message strings (fixes TS2322) */
  getPasswordErr(): string {
    return this.passwordErrorService.getPasswordErr(this.passwordControl);
  }

  getConfirmPwdErr(): string {
    return this.passwordErrorService.getConfirmPwdErr(this.passwordForm.controls[FormControlNames.CONFIRM_PASSWORD]);
  }

  onFocus(): void {
    this.passwordForm.get(FormControlNames.CONFIRM_PASSWORD)?.markAsTouched();
  }

  reset(): void {
    this.passRecoveryService.resetPassword(this.username, this.token, this.resetPassword).subscribe(
      () => this.handleResetSuccess(),
      (error) => this.handleResetError(error),
    );
  }

  /** ---------- Helpers to remove duplication (behavior unchanged) ---------- */

  private handleResetSuccess(): void {
    this.router.navigate(['/']);
    setTimeout(() => {
      this.notificationService.showSnack('PASS_RECOVERY_SUCCESS', 10000, ['snackbar-success']);
    }, 800);
  }

  private handleResetError(error: any): void {
    const errorMessage = error?.error?.message ?? 'PASS_RESET_CHANGED_ERROR';
    this.notificationService.showSnack(errorMessage, 8000, ['snackbar-error']);
  }
}
